import { Immutable } from "immer";
import React from "react";

import type { SpecialOfferList as Value } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { getLinkUrl, originalUrl } from "../../utils/wagtail";

import styles from "./index.module.scss";

type IProps = Immutable<{
    value: Value;
}>;

type IOffer = NonNullable<Immutable<Value>["body"]>[number];
type IOfferChild = NonNullable<IOffer["body"]>[number];

const renderOfferChild = (block: IOfferChild): React.JSX.Element => {
    switch (block.type) {
        case "rich_text":
            // The serialized RichTextBlock string INCLUDES the
            // ``<div class="rich-text">`` wrapper -- exactly what the legacy
            // ``{% include_block %}`` emitted here. The class-less host div
            // is the standard inert dispatch host (toolbox rich_text
            // precedent).
            return (
                <div
                    key={block.id}
                    dangerouslySetInnerHTML={{ __html: block.value }}
                />
            );
        case "cta":
            return (
                <a
                    key={block.id}
                    className={concatClassNames([
                        styles.promotionsCta,
                        "button",
                    ])}
                    href={getLinkUrl(block.value)}
                    title={block.value.html_title ?? ""}
                >
                    {block.value.display_name}
                </a>
            );
        case "legal":
            // Legacy printed ``{{ self.text }}`` WITHOUT the ``|richtext``
            // filter, so it had NO ``.rich-text`` wrapper; the serialized
            // value INCLUDES it -- the legal copy therefore gains one extra
            // ``.rich-text`` wrapper div relative to legacy (screenshot gate
            // adjudicates; do not strip it).
            return (
                <div
                    key={block.id}
                    className={concatClassNames([
                        styles.promotionsLegal,
                        "legal",
                    ])}
                    dangerouslySetInnerHTML={{
                        __html: block.value.text ?? "",
                    }}
                />
            );
    }
};

/**
 * Faithful JSX transcription of the legacy sealy
 * ``components/special_offers/special_offer_list.html`` (+ the
 * ``special_offer_cta.html`` / ``special_offer_legal.html`` child templates)
 * (#33285). The literal ``alt="…"`` on the offer img is preserved exactly as
 * legacy emitted it.
 */
export const SpecialOfferList = (props: IProps): React.JSX.Element => {
    return (
        <>
            <h4>{props.value.title}</h4>
            {(props.value.body ?? []).map((offer, index) => (
                <section className={styles.promotionsPromotion} key={index}>
                    <picture className={styles.promotionsImage}>
                        <source
                            srcSet={originalUrl(offer.mobile_image)}
                            media="(max-width: 960px)"
                        />
                        <img
                            className="img-responsive"
                            srcSet={originalUrl(offer.image)}
                            alt="…"
                            loading="lazy"
                            decoding="async"
                        />
                    </picture>

                    <div className={styles.promotionsContent}>
                        {(offer.body ?? []).map(renderOfferChild)}
                    </div>
                </section>
            ))}
        </>
    );
};
